import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  ContextRail,
  RailBand,
  RailBandHeading,
  RailIcon,
  RailIconGroup,
  RailLink,
} from "@/components/layout/ContextRail";
import { useReportingCurrency } from "@/hooks/useReportingCurrency";
import { deskKeys } from "@/lib/api/queryKeys";
import { deskApi } from "@/lib/api/desk";

import { useDeskOrgLevel } from "../level";
import { useDeskMe } from "../useDeskCan";
import {
  GROUP_LABEL,
  GROUP_ORDER,
  homeFor,
  surfacesFor,
  type SurfaceKey,
} from "../surfaces";
import { useScopeKey } from "@/hooks/useScopeKey";

/**
 * Desk's contextual rail — the surfaces Desk has, not filters over one of them.
 *
 * An earlier version listed boards ("All open", "Unassigned", "Blocked"), which
 * was a filter menu wearing a rail's clothes: every row led to the same table
 * with a different query string, so the rail said nothing about what Desk *is*.
 * Filter presets already have a home — `SavedFilterManager` on the queue, which
 * stores them per user server-side. A rail is for navigation.
 *
 * **Banded, not flat.** The home surface sits alone at the top, then Work —
 * the four cuts of the same pile of tickets a technician moves between all day
 * — then Manage, which is the queue's own machinery: how it is measured, what
 * feeds it, who it routes to. A flat list made those two kinds of thing look
 * interchangeable, so "Reports" sat between "Overdue" and "Automation" as if
 * choosing it were the same sort of move. The bands come from the shared
 * surface table, so the rail cannot invent a grouping the routes do not have.
 *
 * **Mounted beside `SectionRail` in `MainLayout`, not inside the page.** It
 * lived in the page first and was visibly wrong: its right border stopped
 * wherever the table stopped instead of running the height of the surface. The
 * admin rail is a sibling of `<main>`, and that is what makes it full-height.
 *
 * The frame, the fold and the rows are `ContextRail`'s, shared with the
 * credentials page's organizations rail so the two read as one product. The
 * storage keys are the ones this rail always had: a new key would forget every
 * reader's width and fold.
 */

export function DeskRail() {
  const { pathname } = useLocation();
  const { currency } = useReportingCurrency();

  const onDesk = pathname === "/desk" || pathname.startsWith("/desk/");
  const level = useDeskOrgLevel();
  // The rail is mounted by the layout, outside the access gate the pages sit
  // behind, so it asks the same question itself: without `view` it neither
  // fetches counts the server would refuse nor draws sections that lead to
  // the gate's refusal.
  const { data: me } = useDeskMe();
  const canView = me?.actions.includes("view") ?? false;

  // React Query shares these with the pages themselves, so mounting the rail
  // outside the page costs no extra request.
  const scopeKey = useScopeKey();
  const { data: summary } = useQuery({
    queryKey: deskKeys.queueSummary(currency, scopeKey),
    queryFn: () => deskApi.queueSummary(currency),
    enabled: onDesk && canView,
  });
  const { data: runs } = useQuery({
    queryKey: deskKeys.runs(scopeKey),
    queryFn: () => deskApi.listRuns(),
    enabled: onDesk && canView && level !== "company",
  });
  const { data: teams } = useQuery({
    queryKey: deskKeys.teams(scopeKey),
    queryFn: () => deskApi.listTeams(),
    enabled: onDesk && canView && level !== "company",
  });

  // What each level has comes from one table the home page also reads, so
  // the rail cannot offer a surface the route would refuse. A count of
  // `undefined` renders nothing: a number that has not arrived is not a
  // section with no rows.
  const counts: Partial<Record<SurfaceKey, number | undefined>> = {
    home: homeFor(level) === "queue" ? summary?.open : undefined,
    queue: summary?.open,
    teams: teams?.results.length,
    automation: runs?.results.length,
  };
  const items = surfacesFor(level).map((s) => ({ ...s, count: counts[s.key] }));

  if (!onDesk || (me && !canView)) return null;

  const home = items.filter((i) => i.group === null);
  // A level that has nothing in a band gets no heading for it: a company sees
  // no Automation or Teams, and an empty "Manage" label would advertise
  // surfaces it cannot reach.
  const bands = GROUP_ORDER.map((group) => ({
    group,
    items: items.filter((i) => i.group === group),
  })).filter((band) => band.items.length > 0);

  return (
    <ContextRail
      title="Desk"
      label="Desk sections"
      foldLabel="Desk sections"
      widthKey="ec-desk-rail-w-v1"
      foldKey="ec-desk-rail-collapsed-v1"
      defaultWidth={208}
      minWidth={168}
      maxWidth={360}
      // Folded is the same rail with the words removed, not a chevron on an
      // empty strip. Every surface keeps its icon and its count, in the same
      // bands, so a technician who works with the rail folded still sees the
      // queue's size change without opening it.
      folded={
        <>
          <RailIconGroup>
            {home.map((item) => (
              <RailIcon
                key={item.href}
                to={item.href}
                end={item.end}
                icon={item.icon}
                name={item.name}
                count={item.count}
              />
            ))}
          </RailIconGroup>
          {bands.map(({ group, items: inGroup }) => (
            <RailIconGroup key={group} label={GROUP_LABEL[group]} divided>
              {inGroup.map((item) => (
                <RailIcon
                  key={item.href}
                  to={item.href}
                  end={item.end}
                  icon={item.icon}
                  name={item.name}
                  count={item.count}
                />
              ))}
            </RailIconGroup>
          ))}
        </>
      }
    >
      <div className="space-y-0.5">
        {home.map((item) => (
          <RailLink
            key={item.href}
            to={item.href}
            end={item.end}
            icon={item.icon}
            label={item.name}
            count={item.count}
          />
        ))}
      </div>
      {bands.map(({ group, items: inGroup }) => (
        <RailBand
          key={group}
          heading={<RailBandHeading label={GROUP_LABEL[group]} />}
        >
          {inGroup.map((item) => (
            <RailLink
              key={item.href}
              to={item.href}
              end={item.end}
              icon={item.icon}
              label={item.name}
              count={item.count}
            />
          ))}
        </RailBand>
      ))}
    </ContextRail>
  );
}
